import React, { useState } from "react"
import MyModal from "@common-utils/MyModal"
import * as Yup from "yup"
import {
     CONST_BUSINESS_UNIT_AERO_ID,
     CONST_BUSINESS_UNIT_AUTRE_ID,
     CONST_CONSULTANT_MISSION_FREQUENCE_HABITUELLE,
     CONST_CONSULTANT_MISSION_FREQUENCE_OCCASIONNEL,
     CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR,
} from "@common-constants/*"
import { Form, Formik, FormikHelpers } from "formik"
import MyAlert from "@common-utils/MyAlert"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MySelectFormField from "@common-components/fields/MySelectFormField"
import MySelectGetAllDataFromServerFormField from "@common-components/fields/MySelectGetAllDataFromServerFormField"
import MyInputFormField from "@common-components/fields/MyInputFormField"
import MyCheckboxFormField from "@common-components/fields/MyCheckboxFormField"
import MyTextareaFormField from "@common-components/fields/MyTextareaFormField"
import { toast } from "react-toastify"
import { AxiosError } from "axios"
import { IConsultantMissionModel, IConsultantModel, IHttpErrorResponseModel } from "@common-models/*"
import { setConsultantInfoAssurance } from "../../modules/business/consultants/core/_requests"
import { CONST_API_UTILS_GET_BUSINESS_UNITS_ENDPOINT } from "../../endpoints"
import AddMachineForm from "../../modules/utils/_AddMachineForm"

const ConsultantSetInfoAssurance = ({ consultant, mission }: IPropsModel) => {
     const [isAddingMachine, setIsAddingMachine] = useState<boolean>(false)

     // Form schema
     const validationSchema = Yup.object().shape({
          assuranceBu: Yup.number().label("Secteur d'activité").required(),
          assuranceBuAutrePrecision: Yup.string()
               .label("Précisez le secteur")
               .when("assuranceBu", {
                    is: CONST_BUSINESS_UNIT_AUTRE_ID,
                    then: schema => schema.required(),
               }),
          assuranceBuIfAeroIsInterventionDansAvion: Yup.boolean()
               .label("Le consultant intervient dans avion ?")
               .when("assuranceBu", {
                    is: CONST_BUSINESS_UNIT_AERO_ID,
                    then: schema => schema.required(),
               }),
          assuranceBuIfAeroFrequenceInterventionDansAvion: Yup.string()
               .label("Fréquence d'intervention dans l'avion")
               .nullable()
               .when("assuranceBuIfAeroIsInterventionDansAvion", {
                    is: true,
                    then: schema => schema.required(),
               }),
          assuranceIsDetonique: Yup.boolean().label("S'agit t-il d'une Activité détonique / explosif ?").required(),
          assuranceIsBureauEtude: Yup.boolean().label("S'agit t-il d'un bureau d'études ?").required(),
          assuranceIsVehiculeConcerned: Yup.boolean().label("Un Véhicule est concerné ?").required(),
          assuranceTypeOfVehicule: Yup.string()
               .label("Type de véhicule")
               .when("assuranceIsVehiculeConcerned", {
                    is: true,
                    then: schema => schema.required(),
               }),
          assuranceUsageFrequencyOfVehicule: Yup.string()
               .label("Fréquence d'utilisation")
               .when("assuranceIsVehiculeConcerned", {
                    is: true,
                    then: schema => schema.required(),
               }),
          assuranceIsVehiculeGoingToCarryEquipments: Yup.boolean()
               .label("Y a t il des équipements à transporter dans le véhicule ?")
               .when("assuranceIsVehiculeConcerned", {
                    is: true,
                    then: schema => schema.required(),
               }),
          assuranceValueOfEquipementsCarriedByVehicule: Yup.number()
               .label("Montant à assurer")
               .when("assuranceIsVehiculeGoingToCarryEquipments", {
                    is: true,
                    then: schema => schema.required(),
               }),
          assuranceIsEquipmentConcerned: Yup.boolean().label("Un équipement est concerné ?").required(),
          assuranceEquipment: Yup.string()
               .label("Équipement")
               .when("assuranceIsEquipmentConcerned", {
                    is: true,
                    then: schema => schema.required(),
               }),
          assuranceUsageFrequencyOfEquipment: Yup.string()
               .label("Fréquence d'utilisation")
               .when("assuranceIsEquipmentConcerned", {
                    is: true,
                    then: schema => schema.required(),
               }),
          assuranceRemarques: Yup.string().label("Remarques"),
     })

     // Form initial values
     const initialValues: ISetConsultantMissionInfoAssuranceOnlyRequestModel = {
          assuranceBu: mission?.assuranceBu?.id || undefined,
          assuranceBuAutrePrecision: mission?.assuranceBuAutrePrecision || undefined,
          assuranceBuIfAeroIsInterventionDansAvion: !!mission?.assuranceBuIfAeroIsInterventionDansAvion,
          assuranceBuIfAeroFrequenceInterventionDansAvion: mission?.assuranceBuIfAeroFrequenceInterventionDansAvion || undefined,
          assuranceIsDetonique: !!mission?.assuranceIsDetonique,
          assuranceIsBureauEtude: !!mission?.assuranceIsBureauEtude,
          assuranceIsVehiculeConcerned: !!mission?.assuranceIsVehiculeConcerned,
          assuranceTypeOfVehicule: mission?.assuranceTypeOfVehicule || undefined,
          assuranceUsageFrequencyOfVehicule: mission?.assuranceUsageFrequencyOfVehicule || undefined,
          assuranceIsVehiculeGoingToCarryEquipments: !!mission?.assuranceIsVehiculeGoingToCarryEquipments,
          assuranceValueOfEquipementsCarriedByVehicule:
               mission?.assuranceValueOfEquipementsCarriedByVehicule === null ? undefined : mission?.assuranceValueOfEquipementsCarriedByVehicule,
          assuranceIsEquipmentConcerned: !!mission?.assuranceIsEquipmentConcerned,
          assuranceEquipment: mission?.assuranceEquipment || undefined,
          assuranceUsageFrequencyOfEquipment: mission?.assuranceUsageFrequencyOfEquipment || undefined,
          assuranceRemarques: mission?.assuranceRemarques || undefined,
     }

     // Handles submit
     function handleSubmit(values: ISetConsultantMissionInfoAssuranceOnlyRequestModel, helpers: FormikHelpers<ISetConsultantMissionInfoAssuranceOnlyRequestModel>) {
          helpers.setStatus(null)

          setConsultantInfoAssurance(mission.id!, values)
               .then(() => {
                    toast.success("Vos modifications ont bien été prises en compte.")
               })
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data

                    // Set form errors
                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         for (const key in error.errors) helpers.setFieldError(key, error.errors[key])
                    }

                    // Set form global status and notify user using a toast
                    helpers.setStatus(error?.detail)
                    toast.error(error?.detail, { autoClose: false })

                    // Stop submit loader
                    helpers.setSubmitting(false)
               })
               .finally(() => helpers.setSubmitting(false))
     }

     return (
          <>
               <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema} enableReinitialize>
                    {helpers => {
                         console.log(helpers.errors)
                         return (
                              <Form noValidate autoComplete="off">
                                   {helpers.status && (
                                        <MyAlert type={"danger"} classNames={"mb-4"}>
                                             {helpers.status}
                                        </MyAlert>
                                   )}

                                   <MySelectGetAllDataFromServerFormField
                                        name={"assuranceBu"}
                                        formikProps={helpers}
                                        endpoint={CONST_API_UTILS_GET_BUSINESS_UNITS_ENDPOINT}
                                        defaultOptions={
                                             mission?.assuranceBu
                                                  ? [
                                                         {
                                                              value: mission!.assuranceBu!.id!,
                                                              label: mission!.assuranceBu!.nom!,
                                                         },
                                                    ]
                                                  : []
                                        }
                                        placeholder={"Secteur d'activité"}
                                        method={"GET"}
                                        label={<span className={"required"}>Type d'activité</span>}
                                        classNameLayout={"mb-2"}
                                        classNameLabel={"col-lg-4"}
                                        classNameField={"col-lg-8"}
                                   />
                                   {helpers.values.assuranceBu === CONST_BUSINESS_UNIT_AUTRE_ID && (
                                        <MyInputFormField
                                             name={"assuranceBuAutrePrecision"}
                                             formikProps={helpers}
                                             placeholder={"Précisez le secteur"}
                                             label={<span className={"required"}>Précisez le secteur</span>}
                                             classNameLayout={"mb-2"}
                                             classNameLabel={"col-lg-4"}
                                             classNameField={"col-lg-8"}
                                        />
                                   )}

                                   {helpers.values.assuranceBu === CONST_BUSINESS_UNIT_AERO_ID && (
                                        <div className={"p-4 border border-dashed mb-4"}>
                                             <h5 className={"mb-4"}>Aerospace</h5>
                                             <div className="separator separator-dashed mb-6" />
                                             <MyCheckboxFormField
                                                  name={"assuranceBuIfAeroIsInterventionDansAvion"}
                                                  formikProps={helpers}
                                                  label={"Des interventions sont-elles prévues à bord, à proximité ou aux abords de l’avion ?"}
                                                  classNameLayout={"mb-2"}
                                                  classNameLabel={"col-lg-4"}
                                                  classNameField={"col-lg-8"}
                                             />
                                             {helpers.values.assuranceBuIfAeroIsInterventionDansAvion && (
                                                  <MySelectFormField
                                                       name={"assuranceBuIfAeroFrequenceInterventionDansAvion"}
                                                       formikProps={helpers}
                                                       options={[
                                                            { value: CONST_CONSULTANT_MISSION_FREQUENCE_OCCASIONNEL, label: "Occasionnelle" },
                                                            { value: CONST_CONSULTANT_MISSION_FREQUENCE_HABITUELLE, label: "Habituelle" },
                                                       ]}
                                                       placeholder={"Fréquence d'intervention"}
                                                       label={<span className="required">Fréquence d'intervention</span>}
                                                       classNameLayout={"mb-2"}
                                                       classNameLabel={"col-lg-4"}
                                                       classNameField={"col-lg-8"}
                                                  />
                                             )}
                                        </div>
                                   )}

                                   <MyCheckboxFormField
                                        name={"assuranceIsDetonique"}
                                        formikProps={helpers}
                                        label={"Activité détonique / explosif"}
                                        classNameLayout={"mb-2 mt-5"}
                                        classNameLabel={"col-lg-4"}
                                        classNameField={"col-lg-8"}
                                   />
                                   <MyCheckboxFormField
                                        name={"assuranceIsBureauEtude"}
                                        formikProps={helpers}
                                        label={"Bureau d'études"}
                                        classNameLayout={"mb-2 mt-5"}
                                        classNameLabel={"col-lg-4"}
                                        classNameField={"col-lg-8"}
                                   />
                                   <MyCheckboxFormField
                                        name={"assuranceIsVehiculeConcerned"}
                                        formikProps={helpers}
                                        label={`Utilisera t-${consultant!.civilite === "CIVILITE_MR" ? "il" : "elle"} un véhicule client ?`}
                                        classNameLayout={"mb-4 mt-5"}
                                        classNameLabel={"col-lg-4"}
                                        classNameField={"col-lg-8"}
                                   />
                                   {helpers.values.assuranceIsVehiculeConcerned && (
                                        <div className={"p-4 border border-dashed mb-4"}>
                                             <h5 className={"mb-4"}>Véhicule</h5>
                                             <div className="separator separator-dashed mb-6" />
                                             <MyInputFormField
                                                  name={"assuranceTypeOfVehicule"}
                                                  formikProps={helpers}
                                                  placeholder={"Type de véhicule"}
                                                  label={<span className={"required"}>Type de véhicule</span>}
                                                  classNameLayout={"mb-2"}
                                                  classNameLabel={"col-lg-4"}
                                                  classNameField={"col-lg-8"}
                                             />

                                             <MySelectFormField
                                                  name={"assuranceUsageFrequencyOfVehicule"}
                                                  formikProps={helpers}
                                                  options={[
                                                       { value: CONST_CONSULTANT_MISSION_FREQUENCE_OCCASIONNEL, label: "Occasionnelle" },
                                                       { value: CONST_CONSULTANT_MISSION_FREQUENCE_HABITUELLE, label: "Habituelle" },
                                                  ]}
                                                  placeholder={"Fréquence d'utilisation"}
                                                  label={<span className="required">Fréquence d'utilisation</span>}
                                                  classNameLayout={"mb-2"}
                                                  classNameLabel={"col-lg-4"}
                                                  classNameField={"col-lg-8"}
                                             />

                                             <MyCheckboxFormField
                                                  name={"assuranceIsVehiculeGoingToCarryEquipments"}
                                                  formikProps={helpers}
                                                  label={"Y a t-il des équipements à transporter dans le véhicule ?"}
                                                  classNameLayout={"mb-2 mt-5"}
                                                  classNameLabel={"col-lg-4"}
                                                  classNameField={"col-lg-8"}
                                             />

                                             {helpers.values.assuranceIsVehiculeGoingToCarryEquipments && (
                                                  <MyInputFormField
                                                       name={"assuranceValueOfEquipementsCarriedByVehicule"}
                                                       type={"number"}
                                                       formikProps={helpers}
                                                       placeholder={"Montant à garantir des équipements"}
                                                       label={<span className="required">Montant à garantir des équipements</span>}
                                                       classNameLayout={"mb-2"}
                                                       classNameLabel={"col-lg-4"}
                                                       classNameField={"col-lg-8"}
                                                  />
                                             )}
                                        </div>
                                   )}

                                   <MyCheckboxFormField
                                        name={"assuranceIsEquipmentConcerned"}
                                        formikProps={helpers}
                                        label={"Utilisation d'une machine ou équipement client"}
                                        classNameLayout={"mb-4 mt-5"}
                                        classNameLabel={"col-lg-4"}
                                        classNameField={"col-lg-8"}
                                   />
                                   {helpers.values.assuranceIsEquipmentConcerned && (
                                        <div className={"p-4 border border-dashed mb-4"}>
                                             <h5 className={"mb-4"}>Machine / équipement</h5>
                                             <div className="separator separator-dashed mb-6" />
                                             <MyInputFormField
                                                  name={"assuranceEquipment"}
                                                  formikProps={helpers}
                                                  placeholder={"Équipement"}
                                                  label={<span className={"required"}>Machine / Équipement</span>}
                                                  classNameLayout={"mb-2 "}
                                                  classNameLabel={"col-lg-4"}
                                                  classNameField={"col-lg-8"}
                                             />

                                             <MySelectFormField
                                                  name={"assuranceUsageFrequencyOfEquipment"}
                                                  formikProps={helpers}
                                                  options={[
                                                       { value: CONST_CONSULTANT_MISSION_FREQUENCE_OCCASIONNEL, label: "Occasionnelle" },
                                                       { value: CONST_CONSULTANT_MISSION_FREQUENCE_HABITUELLE, label: "Habituelle" },
                                                  ]}
                                                  placeholder={"Fréquence d'utilisation"}
                                                  label={<span className="required">Fréquence d'utilisation</span>}
                                                  classNameLayout={"mb-2"}
                                                  classNameLabel={"col-lg-4"}
                                                  classNameField={"col-lg-8"}
                                             />
                                        </div>
                                   )}

                                   <MyTextareaFormField
                                        name={"assuranceRemarques"}
                                        formikProps={helpers}
                                        placeholder={"Activités annexes / missions complémentaires / précisions sur les activités pouvant nécessiter une assurance"}
                                        label={"Remarques"}
                                        minRows={5}
                                        maxRows={10}
                                        classNameLayout={"mb-2"}
                                        classNameLabel={"col-lg-4"}
                                        classNameField={"col-lg-8"}
                                   />

                                   <div className="separator bg-primary my-5" />

                                   {/* Submit button */}
                                   <div className={"d-flex justify-content-end"}>
                                        <button className={"btn btn-sm btn-primary d-inline-flex align-items-center"} type={"submit"} disabled={helpers.isSubmitting}>
                                             {!helpers.isSubmitting ? (
                                                  <>
                                                       EDITER <span className={"fas fa-edit fs-4 ms-1"}></span>
                                                  </>
                                             ) : (
                                                  <MySimpleSpinner size={"sm"} />
                                             )}
                                        </button>
                                   </div>
                              </Form>
                         )
                    }}
               </Formik>

               {/* Modal add machine */}
               {isAddingMachine && (
                    <MyModal title={<>Nouvelle machine</>} show={true} onCloseCallback={() => setIsAddingMachine(false)}>
                         <AddMachineForm callback={() => setIsAddingMachine(false)} />
                    </MyModal>
               )}
          </>
     )
}

interface IPropsModel {
     consultant: IConsultantModel
     mission: IConsultantMissionModel
}

export interface ISetConsultantMissionInfoAssuranceOnlyRequestModel {
     assuranceBu?: number
     assuranceBuAutrePrecision?: string
     assuranceBuIfAeroIsInterventionDansAvion?: boolean
     assuranceBuIfAeroFrequenceInterventionDansAvion?: string
     assuranceIsDetonique?: boolean
     assuranceIsBureauEtude?: boolean
     assuranceIsVehiculeConcerned?: boolean
     assuranceTypeOfVehicule?: string
     assuranceUsageFrequencyOfVehicule?: string
     assuranceIsVehiculeGoingToCarryEquipments?: boolean
     assuranceValueOfEquipementsCarriedByVehicule?: number
     assuranceIsEquipmentConcerned?: boolean
     assuranceEquipment?: string
     assuranceUsageFrequencyOfEquipment?: string
     assuranceRemarques?: string
}

export default ConsultantSetInfoAssurance
